import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import apiServices from '../../../services/service';
import { getUser } from '../../../redux/selector';
import { Ic } from '../../../shared/component/GosIcon';

/* Change Password — GrowthOS modal (matched to growthos/Users.jsx PasswordModal).
   Rebuilt from the legacy react-bootstrap + Formik modal to the shared .gos-import-* shell +
   a self-contained .gos-cp block (password fields with show/hide eye, a 4-segment strength meter,
   and a required "Reason to change password" note). ALL real logic is preserved:
   - Two modes: admin (changePasswordAdmin=true, from the Users list — sets another user's password,
     userId = selectedUser.userId, no current-password field) and self-service (from the sidebar —
     userId = logged-in user, current password required).
   - Submit → apiServices.updatePassword({ userId, newPassword, updateReason, [oldPassword] }).
   Dropped vs the mock: the "Send reset link instead" link (no real reset-link API). The legacy
   in-modal success screen is replaced by a react-toastify success + close (app convention). */

/* password strength (same categories the legacy getStrength used) */
function strengthOf(pw) {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 8) s += 1;
  if (/[A-Z]/.test(pw)) s += 1;
  if (/[0-9]/.test(pw)) s += 1;
  if (/[^A-Za-z0-9]/.test(pw)) s += 1;
  return s;
}
const STRENGTH_LABELS = ['Too weak', 'Weak', 'Fair', 'Good', 'Strong'];

/* Module-level so it isn't recreated each render (which would drop input focus per keystroke) */
function PwField({ label, value, onChange, shown, onToggle, err, placeholder }) {
  return (
    <div className="gos-cp-field">
      <label className="gos-cp-label">
        {label}
        <span className="gos-cp-req"> *</span>
      </label>
      <div className="gos-cp-pw">
        <input
          className={'gos-cp-input' + (err ? ' err' : '')}
          type={shown ? 'text' : 'password'}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="new-password"
        />
        <button type="button" className="gos-cp-eye" onClick={onToggle} title={shown ? 'Hide' : 'Show'}>
          <Ic name={shown ? 'eyeOff' : 'eye'} size={16} />
        </button>
      </div>
      {err ? <div className="gos-cp-err">{err}</div> : null}
    </div>
  );
}

export default function ChangePasswordModal({ show, onHide, selectedUser, changePasswordAdmin }) {
  const userInfo = useSelector(getUser);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [reason, setReason] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!show) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setReason('');
      setShowCurrent(false);
      setShowNew(false);
      setShowConfirm(false);
      setSubmitting(false);
      setErrors({});
    }
  }, [show]);

  if (!show) return null;

  const score = strengthOf(newPassword);
  const mismatch = confirmPassword && newPassword !== confirmPassword;
  const newValid =
    newPassword.length >= 8 &&
    /[A-Z]/.test(newPassword) &&
    /[0-9]/.test(newPassword) &&
    /[^A-Za-z0-9]/.test(newPassword);
  const ready =
    newValid &&
    newPassword === confirmPassword &&
    !!reason.trim() &&
    (changePasswordAdmin || !!currentPassword) &&
    !submitting;

  const validate = () => {
    const e = {};
    if (!changePasswordAdmin && !currentPassword) e.currentPassword = 'Current password is required';
    if (!newPassword) e.newPassword = 'New password required';
    else if (newPassword.length < 8) e.newPassword = 'At least 8 characters required';
    else if (!/[A-Z]/.test(newPassword)) e.newPassword = 'Must include an uppercase letter';
    else if (!/[0-9]/.test(newPassword)) e.newPassword = 'Must include a number';
    else if (!/[^A-Za-z0-9]/.test(newPassword)) e.newPassword = 'Must include a special character';
    if (!confirmPassword) e.confirmPassword = 'Confirm password required';
    else if (newPassword !== confirmPassword) e.confirmPassword = 'Passwords do not match';
    if (!reason.trim()) e.reason = 'Reason is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = () => {
    if (!validate()) return;
    setSubmitting(true);
    const payload = {
      userId: changePasswordAdmin ? selectedUser?.userId : userInfo?.user?.userId,
      newPassword,
      updateReason: reason,
    };
    if (!changePasswordAdmin) payload.oldPassword = currentPassword;

    apiServices
      .updatePassword(payload)
      .then((response) => {
        if (response?.status === 200) {
          toast.success('Password updated successfully!', { position: 'top-right' });
          onHide();
        } else {
          setErrors({ currentPassword: response?.message || 'Password update failed' });
        }
      })
      .catch((error) => {
        setErrors({
          currentPassword: error?.response?.data?.message || 'Something went wrong. Please try again.',
        });
      })
      .finally(() => setSubmitting(false));
  };

  const targetName = changePasswordAdmin
    ? selectedUser?.display_name || `${selectedUser?.firstName || ''} ${selectedUser?.lastName || ''}`.trim() || 'this user'
    : userInfo?.user?.display_name || 'your account';

  return (
    <div
      className="gos-import-overlay gos-cp"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !submitting) onHide();
      }}
    >
      <div className="gos-import-modal" style={{ maxWidth: 480 }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="gos-import-head">
          <div className="gos-import-head-l">
            <span className="gos-import-step-ic">
              <Ic name="lock" size={18} />
            </span>
            <div>
              <div className="gos-import-title">Change password</div>
              <div className="gos-import-sub">
                {changePasswordAdmin ? `Set a new password for ${targetName}.` : 'Keep your account secure.'}
              </div>
            </div>
          </div>
          <button type="button" className="gos-import-close" onClick={onHide}>
            <Ic name="x" size={18} />
          </button>
        </div>

        <div className="gos-import-body">
          {!changePasswordAdmin && (
            <PwField
              label="Current password"
              value={currentPassword}
              onChange={(v) => {
                setCurrentPassword(v);
                setErrors((p) => ({ ...p, currentPassword: undefined }));
              }}
              shown={showCurrent}
              onToggle={() => setShowCurrent((s) => !s)}
              err={errors.currentPassword}
              placeholder="Enter current password"
            />
          )}

          <PwField
            label="New password"
            value={newPassword}
            onChange={(v) => {
              setNewPassword(v);
              setErrors((p) => ({ ...p, newPassword: undefined }));
            }}
            shown={showNew}
            onToggle={() => setShowNew((s) => !s)}
            err={errors.newPassword}
            placeholder="At least 8 characters"
          />

          {newPassword ? (
            <div className={'gos-cp-strength l' + score}>
              <div className="gos-cp-bars">
                {[0, 1, 2, 3].map((i) => (
                  <span key={i} className={'gos-cp-seg' + (i < score ? ' on' : '')} />
                ))}
              </div>
              <div className="gos-cp-strength-label">{STRENGTH_LABELS[score]}</div>
            </div>
          ) : null}

          <PwField
            label="Confirm new password"
            value={confirmPassword}
            onChange={(v) => {
              setConfirmPassword(v);
              setErrors((p) => ({ ...p, confirmPassword: undefined }));
            }}
            shown={showConfirm}
            onToggle={() => setShowConfirm((s) => !s)}
            err={errors.confirmPassword || (mismatch ? "Passwords don't match" : '')}
            placeholder="Re-enter password"
          />

          <div className="gos-cp-field" style={{ marginTop: 4 }}>
            <label className="gos-cp-label">
              Reason to change password
              <span className="gos-cp-req"> *</span>
            </label>
            <textarea
              className={'gos-cp-textarea' + (errors.reason ? ' err' : '')}
              placeholder="Why is this password being changed?"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setErrors((p) => ({ ...p, reason: undefined }));
              }}
            />
            {errors.reason ? <div className="gos-cp-err">{errors.reason}</div> : null}
          </div>
        </div>

        <div className="gos-import-foot end">
          <div className="gos-foot-actions">
            <button type="button" className="gos-btn gos-btn-ghost" onClick={onHide} disabled={submitting}>
              Cancel
            </button>
            <button type="button" className="gos-btn gos-btn-primary" onClick={submit} disabled={!ready}>
              <Ic name={submitting ? 'lock' : 'check'} size={16} />
              {submitting ? 'Updating…' : 'Update password'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
